/**
 * School year and quarter selection page.
 * Reads available years from service and forwards selection to class step.
 */
import React, { useEffect, useState } from 'react';
import { 
  Container,
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  SelectChangeEvent
} from '@mui/material';
import { classesService } from '../services/api.service';
import { useNavigate, useLocation } from 'react-router-dom';

// Grading periods. Values must match the NeoLMS grading period names
// ("Term 1", "Term 2", "Term 3") since the backend matches on these strings.
const QUARTERS = [
  "Term 1",
  "Term 2",
  "Term 3"
];

/**
 * Step 1 of the workflow: choose school year and quarter.
 */
const SchoolYearSelect: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const prevState = location.state || {};
  
  const [schoolYears, setSchoolYears] = useState<string[]>([]);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState<string>(prevState.schoolYear || '');
  const [selectedQuarter, setSelectedQuarter] = useState<string>(prevState.quarter || '');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSchoolYears();
  }, []);

  const fetchSchoolYears = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const years = await classesService.getSchoolYears();
      setSchoolYears(years);
      
      // If no year is previously selected, set default to current school year
      if (!selectedSchoolYear && years.length > 0) {
        // Find current school year (which should be in the middle of the array)
        const currentYearIndex = Math.floor(years.length / 2);
        setSelectedSchoolYear(years[currentYearIndex]);
      }
    } catch (err) {
      setError('Failed to fetch school years. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSchoolYearChange = (event: SelectChangeEvent) => {
    setSelectedSchoolYear(event.target.value);
  };

  const handleQuarterChange = (event: SelectChangeEvent) => {
    setSelectedQuarter(event.target.value);
  };

  const handleContinue = () => {
    // Navigate to the class selection page with selected values.
    navigate('/class-selection', {
      state: {
        schoolYear: selectedSchoolYear,
        quarter: selectedQuarter
      }
    });
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Select School Year & Term
        </Typography>
        
        <Typography variant="body1" paragraph align="center" sx={{ mb: 4 }}>
          Choose the school year and term to proceed
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={200}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="school-year-label">School Year</InputLabel>
                    <Select
                      labelId="school-year-label"
                      id="school-year-select"
                      value={selectedSchoolYear}
                      label="School Year"
                      onChange={handleSchoolYearChange}
                    >
                      {schoolYears.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="quarter-label">Term</InputLabel>
                    <Select
                      labelId="quarter-label"
                      id="quarter-select"
                      value={selectedQuarter}
                      label="Term"
                      onChange={handleQuarterChange}
                      disabled={!selectedSchoolYear}
                    >
                      {QUARTERS.map((quarter) => (
                        <MenuItem key={quarter} value={quarter}>
                          {quarter}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Box mt={3} display="flex" justifyContent="space-between">
                <Button
                  variant="outlined"
                  onClick={() => window.history.back()}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleContinue}
                  disabled={!selectedSchoolYear || !selectedQuarter}
                >
                  Continue
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default SchoolYearSelect; 